import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getLearningPathActivityId,
  shouldShowSessionProgress,
  trackActivityCompletion,
  trackActivityProgress,
  trackActivityProgressFromTranscript,
  trackActivityStart,
} from '../utilities/activityTracking';

// Realtime mode has no per-turn HTTP response carrying the chat LLM's
// progress, so the transcript is sent for a server-side Gemini estimate —
// throttled to bound the approved per-turn LLM cost.
const REALTIME_TURNS_PER_ESTIMATE = 2;
const REALTIME_MIN_INTERVAL_MS = 15000;
const REALTIME_MESSAGE_WINDOW = 30;

/**
 * In-session activity progress for one attempt (one conversation).
 *
 * The bar value is monotonic within the attempt and always starts at 0 —
 * attempts are redone, never continued. Values are null when the current
 * activity has no session bar.
 *
 * Two tiers of activity:
 * - display (shouldShowSessionProgress): the bar moves — learning-path
 *   activities and the onboarding conversation.
 * - tracked (learning-path only): progress is also persisted through the
 *   /activity-progress endpoint. Onboarding is display-only.
 *
 * @param {() => string|null} getSelectedActivity - returns the currently
 *   selected activity (e.g. "learning-path-3" or "onboarding-intro").
 */
export const useLiveActivityProgress = (getSelectedActivity, onConversationProgress = null) => {
  const [attemptProgressIndex, setAttemptProgressIndex] = useState(null);
  const progressRef = useRef(0);
  const lastPersistedRef = useRef(-1);
  const inFlightRef = useRef(false);
  const pendingSendRef = useRef(false);
  const turnsSinceEstimateRef = useRef(0);
  const lastEstimateAtRef = useRef(0);
  const waitTimerRef = useRef(null);
  const chatHistoryRef = useRef([]);
  const getSelectedActivityRef = useRef(getSelectedActivity);
  getSelectedActivityRef.current = getSelectedActivity;
  const onConversationProgressRef = useRef(onConversationProgress);
  onConversationProgressRef.current = onConversationProgress;

  useEffect(() => () => {
    if (waitTimerRef.current) {
      clearTimeout(waitTimerRef.current);
    }
  }, []);

  const getDisplayActivity = useCallback(() => {
    const selectedActivity = getSelectedActivityRef.current?.();
    return shouldShowSessionProgress(selectedActivity) ? selectedActivity : null;
  }, []);

  const getTrackedActivity = useCallback(() => {
    const selectedActivity = getSelectedActivityRef.current?.();
    return getLearningPathActivityId(selectedActivity) ? selectedActivity : null;
  }, []);

  const setLiveProgress = useCallback((progressIndex) => {
    const normalized = Math.max(0, Math.min(100, Math.round(Number(progressIndex) || 0)));
    const next = Math.max(progressRef.current, normalized);
    progressRef.current = next;
    setAttemptProgressIndex(next);
    return next;
  }, []);

  /**
   * Begins a fresh attempt: bar at 0 for activities with a session bar,
   * hidden otherwise. Records the attempt server-side (learning-path only).
   */
  const startAttempt = useCallback((selectedActivity) => {
    progressRef.current = 0;
    lastPersistedRef.current = -1;
    turnsSinceEstimateRef.current = 0;
    lastEstimateAtRef.current = 0;
    pendingSendRef.current = false;
    chatHistoryRef.current = [];
    onConversationProgressRef.current?.(null);
    setAttemptProgressIndex(shouldShowSessionProgress(selectedActivity) ? 0 : null);
    if (!getLearningPathActivityId(selectedActivity)) {
      return Promise.resolve(null);
    }
    return trackActivityStart(selectedActivity);
  }, []);

  /**
   * Per-turn progress reported by the chat LLM (legacy /upload mode).
   * Updates the bar immediately; persists only increases, and only for
   * tracked (learning-path) activities.
   */
  const reportChatLlmProgress = useCallback((progressIndex) => {
    if (!getDisplayActivity() || !Number.isFinite(Number(progressIndex))) {
      return;
    }

    const displayed = setLiveProgress(progressIndex);
    const trackedActivity = getTrackedActivity();
    if (!trackedActivity || displayed <= lastPersistedRef.current) {
      return;
    }

    lastPersistedRef.current = displayed;
    trackActivityProgress(trackedActivity, displayed)
      .then(result => {
        if (result && !result.estimateFailed) {
          setLiveProgress(result.attemptProgressIndex);
          if (result.conversationProgress) {
            onConversationProgressRef.current?.(result.conversationProgress);
          }
        }
      })
      .catch(error => console.error('Failed to persist chat progress:', error));
  }, [getDisplayActivity, getTrackedActivity, setLiveProgress]);

  const sendTranscriptEstimate = useCallback(() => {
    const trackedActivity = getTrackedActivity();
    if (!trackedActivity) {
      return;
    }

    if (inFlightRef.current) {
      pendingSendRef.current = true;
      return;
    }

    const wait = REALTIME_MIN_INTERVAL_MS - (Date.now() - lastEstimateAtRef.current);
    if (wait > 0) {
      if (!waitTimerRef.current) {
        waitTimerRef.current = setTimeout(() => {
          waitTimerRef.current = null;
          sendTranscriptEstimate();
        }, wait);
      }
      return;
    }

    const messages = chatHistoryRef.current
      .filter(message => message?.role === 'user' || message?.role === 'assistant')
      .slice(-REALTIME_MESSAGE_WINDOW);
    if (messages.length === 0) {
      return;
    }

    inFlightRef.current = true;
    turnsSinceEstimateRef.current = 0;
    lastEstimateAtRef.current = Date.now();

    trackActivityProgressFromTranscript(trackedActivity, messages)
      .then(result => {
        if (result && !result.estimateFailed) {
          setLiveProgress(result.attemptProgressIndex);
          if (result.conversationProgress) {
            onConversationProgressRef.current?.(result.conversationProgress);
          }
        }
      })
      .catch(error => console.error('Failed to estimate activity progress:', error))
      .finally(() => {
        inFlightRef.current = false;
        if (pendingSendRef.current) {
          pendingSendRef.current = false;
          sendTranscriptEstimate();
        }
      });
  }, [getTrackedActivity, setLiveProgress]);

  /**
   * Realtime mode: counts completed (final) user turns and sends the
   * transcript for a server estimate every few turns, throttled, with at
   * most one request in flight. Tracked (learning-path) activities only.
   */
  const onUserTurnCompleted = useCallback((chatHistory) => {
    chatHistoryRef.current = Array.isArray(chatHistory) ? chatHistory : [];
    if (!getTrackedActivity()) {
      return;
    }

    turnsSinceEstimateRef.current += 1;
    if (turnsSinceEstimateRef.current >= REALTIME_TURNS_PER_ESTIMATE) {
      sendTranscriptEstimate();
    }
  }, [getTrackedActivity, sendTranscriptEstimate]);

  /**
   * In-chat completion signal: bar to 100 immediately; completion persisted
   * for tracked (learning-path) activities only.
   */
  const reportCompletion = useCallback(() => {
    if (!getDisplayActivity()) {
      return Promise.resolve(null);
    }

    setLiveProgress(100);
    const trackedActivity = getTrackedActivity();
    if (!trackedActivity) {
      return Promise.resolve(null);
    }

    return trackActivityCompletion(trackedActivity)
      .catch(error => {
        console.error('Failed to persist activity completion:', error);
        return null;
      });
  }, [getDisplayActivity, getTrackedActivity, setLiveProgress]);

  return {
    attemptProgressIndex,
    startAttempt,
    reportChatLlmProgress,
    onUserTurnCompleted,
    reportCompletion,
  };
};
